import browser from "@raiden/library/constants/browser";
import { useApi } from "@raiden/library/hooks/useApi";
import { serverApiFetcher } from "@raiden/library/hooks/useApiFetcher";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import generateFrontUri from "@raiden/library/libraries/utils/generateFrontUrl";
import generatePampaUrl from "@raiden/library/libraries/utils/generatePampaUrl";
import Seo from "../../components/Seo";
import { TheOffice } from "../../containers/TheOffice";

const ALIAS_POST_THE_OFFICE = process.env.NEXT_PUBLIC_POST_ALIAS_THE_OFFICE;

/**
 * @param {object} params
 * @param {number | string} params.postId
 * @returns {string}
 */
function getPostUrl({ postId }) {
  return generateApiUrl({
    id: "@api.posts.view",
    parameters: { postId },
    query: {
      fields: ["post.current_revision", "post.revision.cover"],
    },
  });
}

/**
 * @typedef {object} Props
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>} [postResponse]
 */
/**
 * @param {Props} props
 */
function Page({ postResponse }) {
  const translate = useTranslate();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post>} */
  const { swrResponse: swrResponsePost } = useApi(
    postResponse === undefined && ALIAS_POST_THE_OFFICE !== undefined
      ? getPostUrl({ postId: ALIAS_POST_THE_OFFICE })
      : null,
    {
      swrConfig: {
        fallbackData: postResponse,
      },
    },
  );

  const post = swrResponsePost.data?.data;
  const currentRevision = post?.current_revision;
  const title = translate(currentRevision?.seo_title ?? currentRevision?.title);
  const description = translate(
    currentRevision?.seo_description ?? currentRevision?.summary,
  );
  const cover = currentRevision?.cover;
  const imageUrl = cover
    ? generatePampaUrl({
        image: cover,
        mode: "crop",
        format: "default",
      })
    : undefined;

  return (
    <>
      <Seo
        title={title}
        description={description}
        ogType="article"
        imageUrl={imageUrl}
        links={(locale) =>
          generateFrontUri({
            id: "@front.leCabinet",
            parameters: {
              locale,
            },
          })
        }
        structuredData={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: title,
          image: imageUrl ? [imageUrl] : [],
          datePublished: post?.published_at,
          dateModified: post?.updated_at,
        }}
      />

      <TheOffice swrResponsePost={swrResponsePost} />
    </>
  );
}

export default Page;

/**
 * @typedef {import("../../types/Page").PageInitialProps & Props} InitialProps
 */
/**
 * @param {import("../../types/AppContext").PageContext} context
 * @returns {Promise<InitialProps>}
 */
Page.getInitialProps = async function (context) {
  /** @type {InitialProps} */
  const values = {};

  if (!browser) {
    if (ALIAS_POST_THE_OFFICE !== undefined) {
      values.postResponse = await serverApiFetcher(
        getPostUrl({ postId: ALIAS_POST_THE_OFFICE }),
      ).catch(() => {});
    }
  }

  return values;
};
